import React from 'react';

import Icons from '@/core/components/Icons';
import { useIsLg } from '@/core/hooks/useMediaQuery';
import type { DonationPersonae } from '@/core/lib/new-architecture/domain/donation.domain';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import { DonationState } from '@/core/types/donation';

export interface DonationStateTagProps {
  state: DonationState;
  isIconTag?: boolean;
  personae: DonationPersonae;
  isWinner?: boolean;
}

const DonationStateTag: React.FC<DonationStateTagProps> = ({ state, isIconTag, personae, isWinner = true }) => {
  const { t } = useTranslationContext(['common']);
  const isLg = useIsLg();

  switch (state) {
    case DonationState.MODERATION:
      return isIconTag ? null : (
        <div className="flex max-w-fit items-center gap-1 rounded-1 bg-bg-tag-status-moderation px-2 py-1">
          <p className="text-caption-primary font-medium text-content-tag-status-moderation">{t(`components.donation.details.status.${state}`, { ns: 'common' })}</p>
        </div>
      );
    case DonationState.INITIALIZED:
      return isIconTag ? null : (
        <div className="flex max-w-fit items-center gap-1 rounded-1 bg-bg-tag-status-moderation px-2 py-1">
          <p className="text-caption-primary font-medium text-content-tag-status-moderation">{t(`components.donation.details.status.${state}`, { ns: 'common' })}</p>
        </div>
      );
    case DonationState.RESENT:
      return isIconTag ? null : (
        <div className="flex max-w-fit items-center gap-1 rounded-1 bg-bg-tag-status-pendingMod px-2 py-1">
          <p className="text-caption-primary font-medium text-content-tag-status-pendingMod">{t(`components.donation.details.status.${state}`, { ns: 'common' })}</p>
        </div>
      );
    case DonationState.PUBLISHED:
      return isIconTag ? null : (
        <div className="flex max-w-fit items-center gap-1 rounded-1 bg-bg-tag-status-published px-2 py-1">
          <p className="text-caption-primary font-medium text-content-tag-status-published">{t(`components.donation.details.status.${state}`, { ns: 'common' })}</p>
        </div>
      );
    case DonationState.RESERVED: {
      if (personae === 'giver' && isWinner)
        return (
          <div className={`max-w-fit bg-bg-tag-status-bookedGiver p-1 ${isIconTag ? 'rounded-full' : 'flex items-center gap-1 rounded-1'}`}>
            <Icons icon="lock" size={isLg ? '16' : '12'} weight="bold" color="tag-bookedDistinction" />
            {!isIconTag && <p className="text-caption-primary font-medium text-content-tag-status-bookedDistinction">{t(`components.donation.details.status.${state}`, { ns: 'common' })}</p>}
          </div>
        );
      if (personae === 'taker')
        return (
          <div className={`max-w-fit bg-bg-tag-status-bookedWinner p-1 ${isIconTag ? 'rounded-full' : 'flex items-center gap-1 rounded-1'}`}>
            <Icons icon="lock" size={isLg ? '16' : '12'} weight="bold" color="tag-bookedDistinction" />
            {!isIconTag && <p className="text-caption-primary font-medium text-content-tag-status-bookedDistinction">{t(`components.donation.details.status.${state}`, { ns: 'common' })}</p>}
          </div>
        );
      return (
        <div className={`max-w-fit bg-bg-tag-status-booked p-1 ${isIconTag ? 'rounded-full' : 'flex items-center gap-1 rounded-1'}`}>
          <Icons icon="lock" size={isLg ? '16' : '12'} weight="bold" color="tag-booked" />
          {!isIconTag && <p className="text-caption-primary font-medium text-content-tag-status-booked">{t(`components.donation.details.status.${state}`, { ns: 'common' })}</p>}
        </div>
      );
    }
    case DonationState.ENDED:
      return (
        <div className={`max-w-fit bg-bg-tag-status-finished p-1 ${isIconTag ? 'rounded-full' : 'flex items-center gap-1 rounded-1'}`}>
          <Icons icon="check-square" size={isLg ? '16' : '12'} weight="bold" color="tag-finished" />
          {!isIconTag && <p className="text-caption-primary font-medium text-content-tag-status-finished">{t(`components.donation.details.status.${state}`, { ns: 'common' })}</p>}
        </div>
      );
    case DonationState.REFUSED:
      return isIconTag ? null : (
        <div className="flex max-w-fit items-center gap-1 rounded-1 bg-bg-tag-status-refused px-2 py-1">
          <Icons icon="block" size={isLg ? '16' : '12'} weight="bold" color="tag-refused" />
          <p className="text-caption-primary font-medium text-content-tag-status-refused">{t(`components.donation.details.status.${state}`, { ns: 'common' })}</p>
        </div>
      );
    default:
      return null;
  }
};

export default DonationStateTag;
